import React from 'react'
import { Alert, Checkbox, Flex, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useAuth } from '../../auth'
import { formatPrice } from '../../subscription/pricing'
import { SiteLimit } from '../site'
import { UpgradeButton, useStarterSummary } from './starter-upsell'

export type ExtraWebsiteNoticeProps = {
    limit?: SiteLimit
    confirmed: boolean
    onConfirmedChange: (confirmed: boolean) => void
}

/**
 * When all websites of the plan are in use: the price of an extra website to agree to, or why one cannot be added.
 */
export function ExtraWebsiteNotice({ limit, confirmed, onConfirmedChange }: ExtraWebsiteNoticeProps) {
    const { t } = useTranslation()
    const { websitePlan, hasSubscription } = useAuth()
    const starterSummary = useStarterSummary()

    if (!limit || limit.connected < limit.limit) {
        return null
    }

    // Before a plan, a website can be set up to try Evaa: more come with a plan.
    if (!hasSubscription) {
        return (
            <Alert
                type="warning"
                showIcon
                message={t('Without a plan you can add {{count}} websites', { count: limit.limit })}
                description={<Link to="/settings">{t('Choose a plan to add more.')}</Link>}
            />
        )
    }

    if (!limit.extraAvailable || !limit.extraWebsite) {
        return (
            <Alert
                type="warning"
                showIcon
                message={t('All {{count}} websites of your plan are in use', { count: limit.limit })}
                description={t('Upgrade your plan, or disconnect a website first.')}
            />
        )
    }

    const format = (cents: number) => formatPrice(cents, limit.extraWebsite?.currency ?? 'EUR')
    const price =
        limit.extraWebsite.period === 'yearly'
            ? t('{{price}} per year', { price: format(limit.extraWebsite.amount) })
            : t('{{price}} per month', { price: format(limit.extraWebsite.amount) })

    return (
        <Alert
            type="info"
            showIcon
            message={t('All {{count}} websites of your plan are in use', { count: limit.limit })}
            description={
                <Flex
                    vertical
                    gap="small"
                >
                    <Typography.Text>
                        {t('An extra website costs {{price}}, excl. VAT.', { price })}{' '}
                        {/* Right after the period started the prorated amount is about the full price: then just say so. */}
                        {limit.extraWebsite.chargedNow !== null &&
                        limit.extraWebsite.chargedNow < limit.extraWebsite.amount * 0.95
                            ? t('You pay about {{now}} now, for the rest of this billing period.', {
                                  now: format(limit.extraWebsite.chargedNow),
                              })
                            : t('You pay this now.')}
                    </Typography.Text>
                    <Checkbox
                        checked={confirmed}
                        onChange={(event) => onConfirmedChange(event.target.checked)}
                    >
                        {t('Add an extra website to my plan')}
                    </Checkbox>
                    {/* The Website plan: Starter may be the better deal. */}
                    {websitePlan && starterSummary && (
                        <Flex
                            gap="small"
                            align="center"
                            wrap
                        >
                            <Typography.Text type="secondary">
                                {t('Or switch to Starter.')} {starterSummary}
                            </Typography.Text>
                            <UpgradeButton type="default" />
                        </Flex>
                    )}
                </Flex>
            }
        />
    )
}

/**
 * Whether the website can be added: room in the plan, or an extra website agreed to.
 */
export function canAddWebsite(limit: SiteLimit | undefined, confirmed: boolean): boolean {
    return !limit || limit.connected < limit.limit || (limit.extraAvailable && confirmed)
}
